<template lang="pug">
#move-room-schedule-edit
  .mb-3
  .box-body.register-body
    .note
      span 転床もしくは仮押さえの部屋と日時を修正できます。
    FormLabel(v-model="typeName" keyName="種別")
    FormInputSelect(
      v-model="roomId"
      key-name="部屋" name="room-id"
      :options="roomOptions" :styles="{ 'max-width': '540px' }"
      :validation-rules="{ required: true }" required)
    FormLabel(v-model="residentName" keyName="利用者名")
    FormDatetimePicker(
      v-model="startDatetime" :date.sync="startDatetimeDate" :time.sync="startDatetimeTime"
      placeholder="開始日時" key-name="開始日時" name="start-datetime"
      :validation-rules="{ required: true }" required)
    FormDatetimePicker(
      v-model="endDatetime" :date.sync="endDatetimeDate" :time.sync="endDatetimeTime"
      placeholder="終了日時" key-name="終了日時" name="end-datetime"
      :validation-rules="{ required: true }" required)
  .box-footer.register-footer
    ValidationAlert(v-model="showValidationAlert")
    .buttons.m-0
      button.btn.edit-btn(@click="onUpdate" type="button") 更新
      button.btn.cancel-btn(@click="onCancel" type="button") キャンセル

  RelativeLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import MoveRoomSchedulesService, {
  MoveRoomSchedulesGetModel,
  MoveRoomSchedulesType,
  MoveRoomSchedulesPatchModel,
  MoveRoomSchedulesPatchUrlParam
} from "@/commons/services/MoveRoomSchedules";
import RoomsService, { RoomGetModel, RoomType, UseType } from "@/commons/services/Rooms";
import ResidentsService, { ResidentGetModel } from "@/commons/services/Residents";

import RelativeLoading from "@/pc/basics/RelativeLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";
import FormDatetimePicker from "@/pc/components/forms/FormDatetimePicker.vue";

/**
 * ### 仮押さえ／転床更新編集タブコンポーネント
 */
@Component({
  components: {
    RelativeLoading,
    ValidationAlert,
    FormLabel,
    FormInput,
    FormInputSelect,
    FormDatetimePicker
  },
  $_veeValidate: { validator: "new" }
})
export default class MoveRoomScheduleEditTab extends Vue {
  /** 仮押さえ／転床サービス */
  @Inject("MoveRoomSchedulesService") moveRoomSchedulesService!: MoveRoomSchedulesService;
  /** 部屋サービス */
  @Inject("RoomsService") roomsService!: RoomsService;
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;

  /** 編集前開始日時 */
  @Prop({ required: true })
  beforeStartDatetime!: number;
  /** 編集前部屋ID */
  @Prop({ required: true })
  beforeRoomId!: number;
  /** 編集前利用者ID */
  @Prop({ required: true })
  beforeResidentId!: number;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 更新前仮押さえ／転床情報 */
  beforeMoveRoomSchedule: MoveRoomSchedulesGetModel | null = null;

  /** 種別 */
  typeName: string | null = null;
  /** 利用者名 */
  residentName: string | null = null;

  /** 部屋ID */
  roomId: number | null = null;
  /** 部屋選択項目 */
  roomOptions: Array<{ value: number | string; label: string }> = [];
  /** 利用者名MAP。key：利用者ID、value：利用者名 */
  roomNameMap: Map<number, string> = new Map<number, string>();
  /** 開始日時 */
  startDatetime: number | null = null;
  /** 開始日時(日) */
  startDatetimeDate: string = "";
  /** 開始日時(時) */
  startDatetimeTime: string = "";
  /** 終了日時 */
  endDatetime: number | null = null;
  /** 終了日時(日) */
  endDatetimeDate: string = "";
  /** 終了日時(時) */
  endDatetimeTime: string = "";

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   *
   * - 部屋を取得し、部屋の選択項目を作成する
   * - 更新時に使用する部屋名MAPを作成する
   * - 更新対象の仮押さえ／転床を取得し、画面入力情報を初期化する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const rooms: Array<RoomGetModel> = await this.getRooms();
    const roomOptions: Array<{ value: number | string; label: string }> = this.createRoomOptions(rooms);
    this.roomOptions = roomOptions;

    const roomNameMap: Map<number, string> = this.createRoomNameMap(rooms);
    this.roomNameMap = roomNameMap;

    const moveRoomSchedule: MoveRoomSchedulesGetModel = await this.getMoveRoomSchedule(this.beforeStartDatetime, this.beforeRoomId, this.beforeResidentId);
    this.beforeMoveRoomSchedule = moveRoomSchedule;
    this.initializeInputParams(moveRoomSchedule);

    this.isBusy = false;

    return;
  }

  /**
   * ### 部屋を取得する
   */
  public async getRooms(): Promise<Array<RoomGetModel>> {
    const response: Array<RoomGetModel> = await this.roomsService.gets(userStore);
    return response;
  }

  /**
   * ### 部屋セレクトボックス用項目を作成する
   */
  public createRoomOptions(rooms: Array<RoomGetModel>): Array<{ value: number; label: string }> {
    let roomOptions: Array<{ value: number; label: string }> = [];

    for (const room of rooms) {
      const useType: string = this.convertUseTypeName(room.useType);
      const roomType: string = this.convertRoomTypeName(room.roomType);

      if (room.roomType == RoomType.MULTI_MEN || room.roomType == RoomType.MULTI_WOMEN) {
        roomOptions.push({
          value: room.id,
          label: `${room.name}（${useType}・${roomType}・${room.capacity}人）`
        });
      } else {
        roomOptions.push({
          value: room.id,
          label: `${room.name}（${useType}・${roomType}）`
        });
      }
    }

    return roomOptions;
  }

  /**
   * ### 用途名を返す
   */
  public convertUseTypeName(useType: UseType): string {
    if (useType == UseType.UNMANAGED) {
      return "空床管理対象外";
    } else if (useType == UseType.NORMAL_STAY) {
      return "入居用部屋";
    } else if (useType == UseType.SHORT_STAY) {
      return "ショートステイ";
    } else {
      return "";
    }
  }

  /**
   * ### 部屋種別名を返す
   */
  public convertRoomTypeName(roomType: RoomType): string {
    if (roomType == RoomType.UNMANAGED) {
      return "空床管理対象外";
    } else if (roomType == RoomType.SINGLE) {
      return "個室";
    } else if (roomType == RoomType.DOUBLE) {
      return "２人部屋（夫婦部屋）";
    } else if (roomType == RoomType.MULTI_MEN) {
      return "多床室（男性）";
    } else if (roomType == RoomType.MULTI_WOMEN) {
      return "多床室（女性）";
    } else {
      return "";
    }
  }

  /**
   * ### 部屋名MAPを作成する
   *
   * - 仮押さえ／転床更新時に使用するためにMAPを作成して保持する
   * - key：部屋ID、value：部屋名のMAPを作成する
   */
  public createRoomNameMap(rooms: Array<RoomGetModel>): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();
    for (const room of rooms) {
      map.set(room.id, room.name);
    }

    return map;
  }

  /**
   * ### 仮押さえ／転床を取得する
   */
  public async getMoveRoomSchedule(startDatetime: number, roomId: number, residentId: number): Promise<MoveRoomSchedulesGetModel> {
    const response: MoveRoomSchedulesGetModel = await this.moveRoomSchedulesService.get(userStore, startDatetime, roomId, residentId);
    return response;
  }

  /**
   * ### 画面入力情報を初期化する
   */
  public async initializeInputParams(moveRoomSchedule: MoveRoomSchedulesGetModel): Promise<void> {
    this.typeName = this.convertTypeName(moveRoomSchedule.type);
    this.roomId = moveRoomSchedule.roomId;

    if (moveRoomSchedule.type == MoveRoomSchedulesType.CHANGE_ROOM) {
      const resident: ResidentGetModel = await this.getResident(moveRoomSchedule.residentId);
      this.residentName = resident.name;
    } else {
      this.residentName = moveRoomSchedule.residentName;
    }

    this.startDatetime = moveRoomSchedule.startDatetime;
    this.startDatetimeDate = this.$dateUtil.convertNumberToString(moveRoomSchedule.startDatetime, "yyyy-mm-dd");
    this.startDatetimeTime = this.$dateUtil.convertNumberToString(moveRoomSchedule.startDatetime, "hh:MM:00");

    this.endDatetime = moveRoomSchedule.endDatetime;
    this.endDatetimeDate = this.$dateUtil.convertNumberToString(moveRoomSchedule.endDatetime, "yyyy-mm-dd");
    this.endDatetimeTime = this.$dateUtil.convertNumberToString(moveRoomSchedule.endDatetime, "hh:MM:00");

    return;
  }

  /**
   * ### 種別を種別名に変換する
   */
  public convertTypeName(type: MoveRoomSchedulesType): string {
    if (type == MoveRoomSchedulesType.CHANGE_ROOM) {
      return "転床";
    } else if (type == MoveRoomSchedulesType.TEMPORARY) {
      return "仮押さえ";
    } else {
      return "";
    }
  }

  /**
   * ### 利用者取得
   */
  public async getResident(residentId: number): Promise<ResidentGetModel> {
    const response: ResidentGetModel = await this.residentsService.get(userStore, residentId);
    return response;
  }

  /**
   * ### 更新ボタン押下イベント
   *
   * - バリデーションを実施する
   * - バリデーションエラーならライブラリ経由でエラーメッセージを表示する
   * - エラーでなければ仮押さえ／転床更新用の情報を作成して親コンポーネントに通知する
   */
  public async onUpdate(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    const startEndValidationResult: boolean = this.startEndValidate(this.startDatetime, this.endDatetime);
    const endValidationResult: boolean = this.endValidate(this.endDatetime);
    if (validationResult == false || startEndValidationResult == false || endValidationResult == false) {
      this.showValidationAlert = true;
      return;
    } else {
      this.showValidationAlert = false;
    }

    const beforeMoveRoomSchedule: MoveRoomSchedulesGetModel = this.beforeMoveRoomSchedule as MoveRoomSchedulesGetModel;
    const urlPatam: MoveRoomSchedulesPatchUrlParam = {
      startDatetime: beforeMoveRoomSchedule.startDatetime as number,
      roomId: beforeMoveRoomSchedule.roomId as number,
      residentId: beforeMoveRoomSchedule.residentId ? beforeMoveRoomSchedule.residentId : 0
    };
    const requestBody: MoveRoomSchedulesPatchModel = this.createRequestBody(beforeMoveRoomSchedule);

    this.$emit("onUpdate", { urlPatam, requestBody });

    return;
  }

  /**
   * ### 開始日時・終了日時のバリデーション
   *
   * - 開始日時が終了日時よりも未来ならエラーとして扱う
   * - エラーではない(true)、エラー(false)
   */
  public startEndValidate(startDatetime: number | null, endDatetime: number | null): boolean {
    if (startDatetime == null || endDatetime == null) {
      return true;
    } else {
      if (endDatetime < startDatetime) {
        this.$validator.errors.add({ field: "start-datetime", msg: "開始日時が終了日時より未来です" });
        return false;
      } else {
        return true;
      }
    }
  }

  /**
   * ### 終了日時のバリデーション
   *
   * - 終了日時が当月1日より過去の場合はエラーとして扱う
   * - エラーではない(true)、エラー(false)
   */
  public endValidate(endDatetime: number | null): boolean {
    if (endDatetime == null) {
      return true;
    } else {
      const firstDate: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyymm01000000000");
      if (endDatetime < firstDate) {
        this.$validator.errors.add({ field: "end-datetime", msg: "終了日時が当月1日より過去です" });
        return false;
      } else {
        return true;
      }
    }
  }

  /**
   * ### 仮押さえ／転床情報更新パラメータ作成
   */
  public createRequestBody(beforeMoveRoomSchedule: MoveRoomSchedulesGetModel): MoveRoomSchedulesPatchModel {
    const roomId: number = this.roomId as number;
    const roomName: string | undefined = this.roomNameMap.get(roomId);

    const requestBody: MoveRoomSchedulesPatchModel = {
      newStartDatetime: this.startDatetime as number,
      newEndDatetime: this.endDatetime as number,
      newRoomId: roomId,
      newRoomName: roomName as string,
      newResidentName: this.residentName as string,
      type: beforeMoveRoomSchedule.type,
      updated: beforeMoveRoomSchedule.updated
    };

    return requestBody;
  }

  /**
   * ### キャンセルボタン押下イベント
   *
   * - キャンセルボタン押下を親コンポーネントに通知する
   */
  public onCancel(): void {
    this.$emit("onCancel");
    return;
  }
}
</script>

<style lang="scss" scoped>
#move-room-schedule-edit {
  .note {
    margin: 0 0 20px 20px;
  }

  .box-footer {
    padding: 10px 0 0 0 !important;
  }

  .edit-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
